import React, { useEffect, useState } from 'react';
import { Loader2, User, Phone, Mail, MapPin, Plus, Trash2, Star, StarOff, Save, Search, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/Header';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';

export default function ProfilePage() {
  const { user, refreshMe } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ label: 'Casa', address: '', notes: '', is_default: false });
  const [addLoading, setAddLoading] = useState(false);
  const [addQuote, setAddQuote] = useState(null);
  const [addError, setAddError] = useState('');
  const [pwdOpen, setPwdOpen] = useState(false);

  const loadAddresses = async () => {
    setLoading(true);
    try {
      const r = await api.get('/me/addresses');
      setAddresses(r.data);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAddresses(); }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch('/auth/me', { name, phone });
      await refreshMe();
      toast.success('Profilo aggiornato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSavingProfile(false); }
  };

  const searchAddressForForm = async () => {
    if (!addForm.address.trim()) return;
    setAddLoading(true); setAddError(''); setAddQuote(null);
    try {
      const res = await api.post('/quote', { address: addForm.address });
      setAddQuote(res.data);
      if (!res.data.deliverable) setAddError('Indirizzo fuori area di consegna — puoi comunque salvarlo');
    } catch (e) {
      setAddError(e.response?.data?.detail || 'Errore ricerca indirizzo');
    } finally { setAddLoading(false); }
  };

  const addAddress = async () => {
    if (!addQuote) {
      toast.error('Cerca prima l\'indirizzo per verificarlo');
      return;
    }
    try {
      await api.post('/me/addresses', {
        label: addForm.label || 'Casa',
        address: addQuote.display_name || addForm.address,
        lat: addQuote.lat,
        lng: addQuote.lng,
        notes: addForm.notes,
        is_default: addForm.is_default,
      });
      toast.success('Indirizzo salvato');
      setAddOpen(false);
      setAddForm({ label: 'Casa', address: '', notes: '', is_default: false });
      setAddQuote(null);
      loadAddresses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/me/addresses/${id}`, { is_default: true });
      loadAddresses();
    } catch (e) { toast.error('Errore'); }
  };

  const removeAddress = async (id) => {
    if (!window.confirm('Eliminare questo indirizzo?')) return;
    try {
      await api.delete(`/me/addresses/${id}`);
      loadAddresses();
      toast.success('Indirizzo eliminato');
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <h1 className="font-display text-3xl font-bold mb-6">Il mio profilo</h1>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Profile info */}
          <section className="rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
            <h2 className="font-display text-lg font-bold flex items-center gap-2"><User size={18}/> Dati personali</h2>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Nome e cognome</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className="rounded-xl" data-testid="profile-name-input" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Phone size={12}/> Telefono</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} className="rounded-xl" placeholder="+39 333 123456" data-testid="profile-phone-input" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Mail size={12}/> Email</Label>
                <Input value={user?.email || ''} disabled className="rounded-xl bg-[--brand-surface-2]" />
                <p className="text-xs text-[--brand-muted] mt-1">L'email non è modificabile.</p>
              </div>
              <Button onClick={saveProfile} disabled={savingProfile} className="w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="profile-save-button">
                {savingProfile ? <Loader2 size={16} className="animate-spin mr-2"/> : <Save size={16} className="mr-2"/>}
                Salva modifiche
              </Button>
              <Button variant="secondary" onClick={() => setPwdOpen(true)} className="w-full rounded-xl" data-testid="profile-change-password-button">
                <KeyRound size={16} className="mr-2"/>
                Cambia password
              </Button>
            </div>
          </section>

          {/* Saved addresses */}
          <section className="rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold flex items-center gap-2"><MapPin size={18}/> Indirizzi salvati</h2>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="profile-add-address-button"><Plus size={14} className="mr-1"/>Aggiungi</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo indirizzo</DialogTitle>
                    <DialogDescription>Verifica l'indirizzo di consegna sulla mappa.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Etichetta</Label>
                      <Input value={addForm.label} onChange={e => setAddForm(f => ({ ...f, label: e.target.value }))} placeholder="Casa, Ufficio, ..." className="rounded-xl" data-testid="address-label-input" />
                    </div>
                    <div>
                      <Label>Indirizzo completo</Label>
                      <div className="flex gap-2">
                        <Input value={addForm.address} onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} placeholder="Via Roma 12, Catania" className="rounded-xl" data-testid="address-input"
                               onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchAddressForForm())}/>
                        <Button onClick={searchAddressForForm} disabled={addLoading} className="rounded-xl bg-[--brand-primary]">
                          {addLoading ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>}
                        </Button>
                      </div>
                      {addQuote && (
                        <div className="mt-2 text-xs">
                          <div className="text-[--brand-muted]">{addQuote.display_name}</div>
                          {addQuote.deliverable ? (
                            <Badge className="mt-1 bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30">Zona: {addQuote.zone_name} · €{addQuote.delivery_fee?.toFixed(2)}</Badge>
                          ) : (
                            <Badge variant="secondary" className="mt-1">Fuori zona</Badge>
                          )}
                        </div>
                      )}
                      {addError && <div className="text-xs text-[--brand-danger] mt-1">{addError}</div>}
                    </div>
                    <div>
                      <Label>Note (opzionale)</Label>
                      <Textarea value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="rounded-xl" placeholder="Es. citofono rotto, scala B" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={addForm.is_default} onChange={e => setAddForm(f => ({ ...f, is_default: e.target.checked }))} className="accent-[--brand-primary]" />
                      Imposta come indirizzo predefinito
                    </label>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setAddOpen(false)}>Annulla</Button>
                    <Button onClick={addAddress} disabled={!addQuote} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="address-save-button">Salva indirizzo</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-3 space-y-2">
              {loading && <div className="text-sm text-[--brand-muted]">Caricamento...</div>}
              {!loading && addresses.length === 0 && (
                <div className="rounded-xl bg-[--brand-surface-2] p-4 text-sm text-[--brand-muted] text-center">
                  Nessun indirizzo salvato. Aggiungine uno per checkout più veloce.
                </div>
              )}
              {addresses.map(a => (
                <div key={a.id} className="rounded-xl border border-[--brand-border] p-3" data-testid="profile-address-item">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{a.label}</span>
                        {a.is_default && <Badge className="bg-[--brand-primary] text-white border-transparent text-xs">Predefinito</Badge>}
                      </div>
                      <div className="text-xs text-[--brand-muted] mt-0.5 truncate">{a.address}</div>
                      {a.notes && <div className="text-xs text-[--brand-muted] mt-0.5 italic">{a.notes}</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!a.is_default && (
                        <Button variant="ghost" size="icon" onClick={() => setDefault(a.id)} title="Rendi predefinito" data-testid="profile-address-default">
                          <StarOff size={16}/>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => removeAddress(a.id)} className="text-[--brand-danger]" title="Elimina" data-testid="profile-address-delete">
                        <Trash2 size={16}/>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
